'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import {
  Dumbbell, Waves, Sparkles, Car, Shield, Users, Package,
  Bike, ArrowRight, AlertCircle, Loader2, Star, Wind, Coffee
} from 'lucide-react';

interface AmenityItem {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  details: string[];
  accent: string;
  tag?: string;
}

const amenities: AmenityItem[] = [
  {
    id: 'fitness',
    icon: Dumbbell,
    title: 'Fitness Center',
    description: 'A well-equipped fitness facility designed for residents who take their health seriously.',
    details: [
      'Cardio and strength equipment',
      'Available to all verified residents',
      'Panoramic views from upper-floor location',
      'Complements the active Panorama lifestyle',
    ],
    accent: '#4CC9FF',
    tag: 'Wellness',
  },
  {
    id: 'pool',
    icon: Waves,
    title: 'Resort-Style Pool',
    description: 'A signature outdoor pool experience that sets Panorama apart from standard high-rise living.',
    details: [
      'Dual pool areas across both towers',
      'Lounge seating and sun deck',
      'Resident-only access',
      'Year-round Las Vegas climate',
    ],
    accent: '#4CC9FF',
    tag: 'Leisure',
  },
  {
    id: 'spa',
    icon: Sparkles,
    title: 'Spa & Relaxation',
    description: 'Dedicated spa facilities for recovery, relaxation, and the kind of daily reset that high-rise living should offer.',
    details: [
      'Spa amenities within the building',
      'Designed for resident wellness',
      'Complements the fitness and pool experience',
      'A quiet counterpoint to Las Vegas energy',
    ],
    accent: '#7A5CFF',
    tag: 'Wellness',
  },
  {
    id: 'massage',
    icon: Wind,
    title: 'Massage Rooms',
    description: 'Private massage rooms available within the building — a rare amenity that reflects the Panorama standard.',
    details: [
      'Private treatment rooms',
      'Available for resident use',
      'Coordinate through concierge',
      'Part of the broader wellness offering',
    ],
    accent: '#7A5CFF',
    tag: 'Wellness',
  },
  {
    id: 'valet',
    icon: Car,
    title: 'Valet-Supported Convenience',
    description: 'Valet service that removes the friction of daily arrivals and departures — a meaningful quality-of-life upgrade.',
    details: [
      'Valet-supported entry and exit',
      'Available for residents and guests',
      'Reduces parking stress in a busy corridor',
      'Part of the premium Panorama arrival experience',
    ],
    accent: '#FF3DF5',
    tag: 'Convenience',
  },
  {
    id: 'gate',
    icon: Shield,
    title: 'Guarded Entry',
    description: 'A staffed guard gate provides a controlled entry point — adding a layer of privacy and peace of mind for residents.',
    details: [
      'Staffed guard gate at community entry',
      'Controlled access for residents and guests',
      'Contributes to the private, community feel',
      'Standard for premium Las Vegas high-rise living',
    ],
    accent: '#FF3DF5',
    tag: 'Access',
  },
  {
    id: 'concierge',
    icon: Star,
    title: 'Concierge-Supported Lifestyle',
    description: 'A concierge team that helps residents navigate building life, coordinate guests, and handle day-to-day needs.',
    details: [
      'Front desk and concierge staff on site',
      'Package handling and guest coordination',
      'A resource for building questions and logistics',
      'Friendly, professional, and resident-focused',
    ],
    accent: '#4CC9FF',
    tag: 'Service',
  },
  {
    id: 'lobby',
    icon: Coffee,
    title: 'Lobby & Lounge Areas',
    description: 'Thoughtfully designed common areas that create a sense of arrival and community — not just a pass-through.',
    details: [
      'Architecturally distinctive lobby spaces',
      'Sitting areas and lounge feel',
      'A natural gathering point for residents',
      'Reflects the AIA award-winning design standard',
    ],
    accent: '#7A5CFF',
    tag: 'Community',
  },
  {
    id: 'townhomes',
    icon: Users,
    title: 'Townhomes',
    description: 'Panorama includes a collection of townhome residences alongside the tower units — offering a distinct living option within the same community.',
    details: [
      'Ground-level townhome residences',
      'Private entry and distinct layout',
      'Access to all shared community amenities',
      'A different scale within the same premium community',
    ],
    accent: '#4CC9FF',
    tag: 'Residences',
  },
  {
    id: 'storage',
    icon: Package,
    title: 'Resident Storage',
    description: 'Dedicated storage areas for residents — a practical amenity that makes high-rise living more livable.',
    details: [
      'Lower-level resident storage units',
      'Available for select residences',
      'Useful for second-home owners and full-time residents alike',
      'Adds practical value to tower living',
    ],
    accent: '#A7A3C4',
    tag: 'Convenience',
  },
  {
    id: 'bikes',
    icon: Bike,
    title: 'Bike Storage & Racks',
    description: 'Secure bike storage for residents who want to explore Las Vegas on two wheels or maintain an active lifestyle.',
    details: [
      'Dedicated bike storage area',
      'Secure and resident-accessible',
      'Supports an active, urban lifestyle',
      'Convenient for the Dean Martin corridor',
    ],
    accent: '#A7A3C4',
    tag: 'Convenience',
  },
];

type PageState = 'loading' | 'populated' | 'unavailable';

export default function AmenitiesPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [activeTag, setActiveTag] = useState<string>('All');

  useEffect(() => {
    const t = setTimeout(() => setPageState('populated'), 900);
    return () => clearTimeout(t);
  }, []);

  const tags = ['All', 'Wellness', 'Leisure', 'Convenience', 'Access', 'Service', 'Community', 'Residences'];
  const filtered = activeTag === 'All' ? amenities : amenities.filter((a) => a.tag === activeTag);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="transparent" />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden panorama-hero-bg">
          <div
            className="absolute top-0 right-0 w-[55%] h-[80%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 60% at 95% 5%, rgba(76,201,255,0.18) 0%, rgba(122,92,255,0.06) 50%, transparent 70%)' }}
          />
          <div
            className="absolute bottom-0 left-0 w-[45%] h-[50%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 80% 70% at 0% 100%, rgba(255,61,245,0.10) 0%, transparent 65%)' }}
          />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
              style={{ borderRadius: '6px', background: 'rgba(76,201,255,0.10)', border: '1px solid rgba(76,201,255,0.22)' }}
            >
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#4CC9FF' }} />
              <span className="text-[12px] font-medium tracking-wide" style={{ color: '#4CC9FF' }}>
                Amenities & Lifestyle
              </span>
            </div>
            <h1
              className="font-display font-extrabold text-[48px] sm:text-[60px] lg:text-[68px] leading-[1.04] tracking-tight mb-5 max-w-3xl"
              style={{ color: '#F2F4FF' }}
            >
              Built for how{' '}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #4CC9FF 0%, #7A5CFF 100%)' }}>
                you actually live.
              </span>
            </h1>
            <p className="text-[18px] leading-relaxed max-w-2xl mb-10 font-light" style={{ color: 'rgba(242,244,255,0.72)' }}>
              Panorama Towers offers a resident-focused amenity experience that goes well beyond the standard Las Vegas high-rise. From the pool deck to the concierge desk, every detail is designed to support a premium, convenient lifestyle.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/request-access" className="btn-primary group">
                Request Access
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link href="/why-panorama" className="btn-secondary">
                Why Panorama
              </Link>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 neon-divider" />
        </section>

        {/* Loading state */}
        {pageState === 'loading' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Loader2 size={32} className="animate-spin" style={{ color: '#4CC9FF' }} />
                <p className="text-[15px]" style={{ color: '#A7A3C4' }}>Loading amenities…</p>
              </div>
            </div>
          </section>
        )}

        {/* Unavailable state */}
        {pageState === 'unavailable' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.20)' }}>
                  <AlertCircle size={22} style={{ color: '#FF3DF5' }} />
                </div>
                <h3 className="font-display font-semibold text-[20px]" style={{ color: '#F2F4FF' }}>Amenities information unavailable</h3>
                <p className="text-[14px] max-w-sm" style={{ color: '#A7A3C4' }}>We're having trouble loading this content. Please check back shortly.</p>
                <Link href="/" className="btn-secondary text-[13px] px-4 py-2 mt-2">Return Home</Link>
              </div>
            </div>
          </section>
        )}

        {/* Populated state */}
        {pageState === 'populated' && (
          <>
            {/* Filter row */}
            <section style={{ background: '#0C0A16', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 overflow-x-auto">
                <div className="flex gap-2 min-w-max">
                  {tags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setActiveTag(tag)}
                      className="px-3.5 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-150"
                      style={
                        activeTag === tag
                          ? { background: 'rgba(76,201,255,0.12)', color: '#4CC9FF', border: '1px solid rgba(76,201,255,0.25)' }
                          : { background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.07)' }
                      }
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Image placeholder banner */}
            <section style={{ background: '#0C0A16' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-10">
                <div
                  className="w-full h-48 sm:h-64 rounded-xl flex flex-col items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(76,201,255,0.06) 0%, rgba(122,92,255,0.08) 50%, rgba(255,61,245,0.05) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-3"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(167,163,196,0.30)" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="m21 15-5-5L5 21" />
                    </svg>
                  </div>
                  <p className="text-[13px] font-medium" style={{ color: 'rgba(167,163,196,0.40)' }}>Amenity photography coming soon</p>
                  <p className="text-[11px] mt-1" style={{ color: 'rgba(167,163,196,0.25)' }}>Pool, fitness center, spa, and lobby imagery</p>
                </div>
              </div>
            </section>

            {/* Amenities grid */}
            <section className="py-16" style={{ background: '#0C0A16' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filtered.map((amenity) => (
                    <div key={amenity.id} className="neon-card p-7 flex flex-col gap-4">
                      <div className="flex items-start justify-between">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ background: `${amenity.accent}12`, border: `1px solid ${amenity.accent}22` }}
                        >
                          <amenity.icon size={18} style={{ color: amenity.accent }} />
                        </div>
                        {amenity.tag && (
                          <span
                            className="text-[11px] font-medium px-2 py-0.5 rounded"
                            style={{ background: `${amenity.accent}10`, color: amenity.accent, border: `1px solid ${amenity.accent}20` }}
                          >
                            {amenity.tag}
                          </span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-[17px] mb-2" style={{ color: '#F2F4FF' }}>
                          {amenity.title}
                        </h3>
                        <p className="text-[13.5px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                          {amenity.description}
                        </p>
                      </div>
                      <ul className="space-y-2 mt-auto">
                        {amenity.details.map((d, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: amenity.accent }} />
                            <span className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.80)' }}>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Escalade Limo section */}
            <section className="py-16" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-16">
                <div
                  className="relative overflow-hidden panorama-premium-bg rounded-xl p-10 lg:p-14"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div
                    className="absolute top-0 right-0 w-[50%] h-full pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse 80% 70% at 100% 20%, rgba(122,92,255,0.14) 0%, transparent 65%)' }}
                  />
                  <div className="relative z-10 max-w-2xl">
                    <div
                      className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
                      style={{ borderRadius: '6px', background: 'rgba(122,92,255,0.10)', border: '1px solid rgba(122,92,255,0.22)' }}
                    >
                      <Star size={12} style={{ color: '#7A5CFF' }} />
                      <span className="text-[12px] font-medium tracking-wide" style={{ color: '#7A5CFF' }}>
                        Distinctive Panorama Culture
                      </span>
                    </div>
                    <h2 className="font-display font-bold text-[32px] sm:text-[38px] leading-tight tracking-tight mb-5" style={{ color: '#F2F4FF' }}>
                      The Escalade Experience
                    </h2>
                    <p className="text-[16px] leading-relaxed mb-4" style={{ color: 'rgba(167,163,196,0.90)' }}>
                      Part of what makes Panorama distinctive is the culture that residents have built around it. The community is home to two Cadillac Escalade limos — a reflection of the kind of lifestyle and social energy that defines life here.
                    </p>
                    <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'rgba(167,163,196,0.75)' }}>
                      Whether it's a night out on the Strip, a group outing to a Golden Knights game, or a Sphere event with neighbors, the Escalades are part of the Panorama story — a symbol of the premium, community-oriented lifestyle that residents enjoy.
                    </p>
                    <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.50)' }}>
                      Details around availability and coordination are part of the resident community experience. Access the private platform to learn more.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-20" style={{ background: '#0C0A16' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div className="text-center mb-10">
                  <h2 className="font-display font-bold text-[32px] sm:text-[40px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                    Interested in living at Panorama?
                  </h2>
                  <p className="text-[16px] max-w-xl mx-auto mb-8" style={{ color: '#A7A3C4' }}>
                    Explore ownership and rental opportunities, or request access to the private resident community platform.
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-4">
                    <Link href="/request-access" className="btn-primary group">
                      Request Access
                      <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                    <Link href="/real-estate" className="btn-secondary">
                      Explore Real Estate
                    </Link>
                  </div>
                </div>

                {/* Cross-links */}
                <div
                  className="mt-14 pt-10 border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                >
                  <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-5 text-center" style={{ color: 'rgba(167,163,196,0.35)' }}>
                    Explore more
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <Link href="/why-panorama" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Why Panorama
                    </Link>
                    <Link href="/lifestyle" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Lifestyle
                    </Link>
                    <Link href="/community" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Community
                    </Link>
                    <Link href="/nearby" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      What's Nearby
                    </Link>
                    <Link href="/faq" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      FAQ
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
